import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlaidLink } from 'react-plaid-link';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, Landmark, PiggyBank, Target, Users, ShieldCheck, Plus, Trash2, Pencil,
  Search, MapPin, Phone, Globe, Navigation, Star, ChevronDown, Info, Bookmark, X, Send, Clock, Building2, RefreshCw, Link2,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const authH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` } });

const C = { navy: '#2B4C7E', navy2: '#1E3A63', gold: '#C8A97E', cream: '#FDFBF7', green: '#7FA98C', ink: '#1C252E', mute: '#8D8880', line: '#E3E1DC' };

const WARNINGS = [
  'EasyPassing does not hold, manage, invest, or distribute your money.',
  'Your money remains with your chosen bank or financial institution.',
  'Beneficiary instructions saved here may not replace official bank beneficiary forms, payable-on-death designations, wills, trusts, or legal estate documents.',
  'Your Primary Executor should consult the bank and appropriate legal professionals before attempting to distribute funds.',
  'The Release button sends information and instructions. It does not transfer money.',
];

const FAQS = [
  ['What is a traditional savings account?', 'A deposit account at a bank or credit union that earns interest and keeps your money safe and accessible while separating it from everyday spending.'],
  ['What is a high-yield savings account?', 'A savings account that pays a higher interest rate than a traditional one — often offered by online banks — helping your emergency fund grow faster.'],
  ['What is a money market account?', 'A deposit account that typically earns higher interest and may offer limited check-writing or debit access, blending features of savings and checking accounts.'],
  ['What is a certificate of deposit?', 'A CD holds a fixed amount for a set term (e.g., 6–60 months) at a fixed rate. You earn more interest in exchange for leaving the money untouched until maturity.'],
  ['What is a payable-on-death account?', 'A POD (or "Totten trust") designation lets you name a beneficiary directly with your bank. On your passing, funds transfer to them without probate — this is set up with the bank, not EasyPassing.'],
  ['Why should I speak to a bank or estate attorney?', 'Only official bank beneficiary forms, POD designations, wills, and trusts create legally binding transfers. A professional ensures your wishes are enforceable.'],
  ['Does EasyPassing hold my money?', 'No. EasyPassing does not hold, custody, transfer, or distribute your money. Your funds remain with your chosen bank or financial institution.'],
  ['Does EasyPassing distribute funds?', 'No. EasyPassing helps communicate your instructions and account location information to your selected beneficiaries after your Primary Executor completes the release process. Actual distribution of funds must be handled through the bank, estate process, payable-on-death designation, legal representative, or other lawful method.'],
];

const EDU_TOPICS = [
  'What is a savings account?', 'Why families should maintain emergency savings',
  'Differences between savings, money market, CDs & high-yield accounts', 'Importance of FDIC or NCUA insurance',
  'Naming beneficiaries directly with your bank when available',
  'Why a will, trust, POD designation, or official beneficiary form may still be needed',
  'Why your Primary Executor should know where accounts are held',
];

const ACCOUNT_TYPES = [['savings', 'Traditional Savings'], ['high_yield', 'High-Yield Savings'], ['money_market', 'Money Market'], ['cd', 'Certificate of Deposit'], ['checking', 'Checking'], ['other', 'Other']];
const DEPOSIT_METHODS = [['direct_deposit', 'Direct deposit'], ['bank_transfer', 'Bank transfer'], ['cash_deposit', 'Cash deposit'], ['check_deposit', 'Check deposit'], ['manual_entry', 'Manual entry'], ['other', 'Other']];
const DIST_TYPES = [['percentage', 'Percentage'], ['fixed_amount', 'Fixed amount'], ['equal_share', 'Equal share'], ['instructions_only', 'Instructions only']];

export default function SavingsBeneficiaries() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [goals, setGoals] = useState([]);
  const [deposits, setDeposits] = useState({ deposits: [], total_deposited: 0 });
  const [bens, setBens] = useState({ beneficiaries: [], total_percentage: 0, allocation_warning: null });
  const [execs, setExecs] = useState([]);
  const [aftercare, setAftercare] = useState(null);
  const [myBanks, setMyBanks] = useState([]);

  const load = useCallback(async () => {
    try {
      const [a, g, d, b, e, ac, mb] = await Promise.all([
        axios.get(`${API}/savings/accounts`, authH()),
        axios.get(`${API}/savings/goals`, authH()),
        axios.get(`${API}/savings/deposits`, authH()),
        axios.get(`${API}/savings/beneficiaries`, authH()),
        axios.get(`${API}/savings/executors`, authH()),
        axios.get(`${API}/savings/aftercare`, authH()),
        axios.get(`${API}/savings/my-banks`, authH()),
      ]);
      setAccounts(a.data.accounts); setGoals(g.data.goals); setDeposits(d.data);
      setBens(b.data); setExecs(e.data.executors); setAftercare(ac.data); setMyBanks(mb.data.banks);
    } catch (err) {
      if (err?.response?.status === 401) navigate('/login');
      else toast.error('Could not load your savings data');
    } finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => { if (!localStorage.getItem('session_token')) { navigate('/login'); return; } load(); }, [load, navigate]);

  // Handle return from aftercare Stripe checkout
  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get('aftercare_session');
    if (!sid) return;
    axios.get(`${API}/membership/aftercare/status/${sid}`, authH())
      .then((r) => { if (r.data.payment_status === 'paid') { toast.success('Aftercare extension activated'); load(); } })
      .catch(() => {})
      .finally(() => window.history.replaceState({}, '', '/savings'));
  }, [load]);

  const buyAftercare = async (optionId) => {
    try {
      const r = await axios.post(`${API}/membership/aftercare/checkout`, { option_id: optionId, origin: window.location.origin }, authH());
      if (r.data.url) window.location.href = r.data.url;
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not start checkout'); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center" style={{ background: C.cream }}><Loader2 className="h-8 w-8 animate-spin" style={{ color: C.navy }} /></div>;

  return (
    <div className="min-h-screen overflow-x-hidden" style={{ background: C.cream }} data-testid="savings-page">
      <header className="bg-white border-b border-[#EDE7DC] sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-3">
          <button onClick={() => navigate('/dashboard')} className="shrink-0" data-testid="savings-back"><ArrowLeft className="h-5 w-5" style={{ color: C.mute }} /></button>
          <PiggyBank className="h-6 w-6 shrink-0" style={{ color: C.gold }} />
          <h1 className="text-lg font-bold truncate" style={{ color: C.ink }}>Savings &amp; Beneficiary Planning</h1>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        <div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold" style={{ color: C.ink }}>Savings &amp; Beneficiary Planning</h2>
          <p className="mt-1.5 text-sm sm:text-base" style={{ color: '#5b6570' }}>Organize your savings account information, track balances, and prepare clear instructions for the people you trust.</p>
        </div>

        {/* Warnings */}
        <div className="rounded-2xl border p-4 sm:p-5" style={{ background: '#FFF9EC', borderColor: '#F0E2C0' }} data-testid="savings-warnings">
          <div className="flex items-center gap-2 mb-2"><Info className="h-4 w-4" style={{ color: C.gold }} /><p className="font-semibold text-sm" style={{ color: '#8a6d1f' }}>Important — please read</p></div>
          <ul className="space-y-1.5">
            {WARNINGS.map((w, i) => <li key={i} className="text-xs sm:text-sm flex gap-2" style={{ color: '#7a6526' }}><span>•</span>{w}</li>)}
          </ul>
        </div>

        <Education />
        <BankFinder myBanks={myBanks} reloadBanks={load} />
        <LinkedBanks />
        <SavingsAccounts accounts={accounts} reload={load} />
        <SavingsGoals goals={goals} accounts={accounts} reload={load} />
        <Deposits deposits={deposits} accounts={accounts} reload={load} />
        <Beneficiaries bens={bens} reload={load} />
        <Executors execs={execs} reload={load} />
        <Aftercare aftercare={aftercare} navigate={navigate} onBuy={buyAftercare} />

        <p className="text-center text-xs pt-2 pb-8" style={{ color: C.mute }}>
          EasyPassing is not a bank, executor, trustee, financial advisor, or legal estate-planning service. Please consult a qualified attorney or financial professional.
        </p>
      </div>
    </div>
  );
}

// ---------- Linked bank accounts (Plaid) ----------
function LinkedBanks() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [linkToken, setLinkToken] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (refresh = false) => {
    try {
      const r = await axios.get(`${API}/savings/plaid/accounts${refresh ? '?refresh=true' : ''}`, authH());
      setItems(r.data.items || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startLink = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/savings/plaid/link-token`, {}, authH());
      setLinkToken(r.data.link_token);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not start bank linking'); setBusy(false); }
  };

  const onSuccess = useCallback(async (public_token, metadata) => {
    try {
      await axios.post(`${API}/savings/plaid/exchange`, {
        public_token,
        institution_name: metadata?.institution?.name,
        institution_id: metadata?.institution?.institution_id,
      }, authH());
      toast.success('Bank linked');
      setLinkToken(null); setBusy(false);
      load(true);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not link bank'); setBusy(false); }
  }, [load]);

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess,
    onExit: () => { setLinkToken(null); setBusy(false); },
  });

  useEffect(() => { if (linkToken && ready) open(); }, [linkToken, ready, open]);

  const removeItem = async (itemId) => {
    if (!window.confirm('Unlink this bank? Its balances will no longer be imported.')) return;
    try { await axios.delete(`${API}/savings/plaid/item/${itemId}`, authH()); toast.success('Bank unlinked'); load(); }
    catch { toast.error('Could not unlink'); }
  };

  const total = items.reduce((s, it) => s + (it.accounts || []).reduce((a, c) => a + (c.current_balance || 0), 0), 0);

  return (
    <Card testid="linked-banks">
      <SectionHead icon={Link2} title="Linked bank accounts" sub="Securely connect a bank to auto-import balances. Powered by Plaid — EasyPassing never sees your login."
        action={<AddBtn onClick={startLink} label={busy ? 'Opening…' : 'Link a bank'} testid="plaid-link-btn" />} />
      {loading ? (
        <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin" style={{ color: C.navy }} /></div>
      ) : items.length === 0 ? (
        <p className="text-sm" style={{ color: C.mute }} data-testid="linked-banks-empty">No banks linked yet. Link a bank to see live balances alongside your manual accounts.</p>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm" style={{ color: C.mute }}>Total linked balance</p>
            <div className="flex items-center gap-3">
              <p className="font-bold" style={{ color: C.ink }} data-testid="linked-banks-total">{money(total)}</p>
              <button onClick={() => load(true)} className="text-xs flex items-center gap-1" style={{ color: C.navy }} data-testid="plaid-refresh"><RefreshCw className="h-3.5 w-3.5" /> Refresh</button>
            </div>
          </div>
          {items.map((it) => (
            <div key={it.item_id} className="rounded-xl border p-4" style={{ borderColor: C.line }} data-testid="linked-bank-item">
              <div className="flex items-center justify-between mb-2">
                <p className="font-semibold flex items-center gap-2" style={{ color: C.ink }}><Building2 className="h-4 w-4" style={{ color: C.navy }} /> {it.institution_name}</p>
                <button onClick={() => removeItem(it.item_id)} className="text-xs flex items-center gap-1" style={{ color: '#C0563F' }} data-testid="plaid-unlink"><Trash2 className="h-3.5 w-3.5" /> Unlink</button>
              </div>
              <div className="space-y-1.5">
                {(it.accounts || []).map((a) => (
                  <div key={a.account_id} className="flex items-center justify-between text-sm">
                    <span style={{ color: '#57534E' }}>{a.name}{a.mask ? ` ••••${a.mask}` : ''} <span className="text-xs" style={{ color: C.mute }}>({a.subtype})</span></span>
                    <span className="font-semibold" style={{ color: C.ink }}>{money(a.current_balance)}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}


const Card = ({ children, testid }) => <section className="bg-white rounded-2xl border border-[#EDE7DC] p-5 sm:p-6" data-testid={testid}>{children}</section>;
const SectionHead = ({ icon: Icon, title, sub, action }) => (
  <div className="flex items-start justify-between gap-3 mb-4">
    <div className="flex items-start gap-3 min-w-0">
      <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: '#EBF0F5' }}><Icon className="h-5 w-5" style={{ color: C.navy }} /></div>
      <div className="min-w-0"><h3 className="font-bold" style={{ color: C.ink }}>{title}</h3>{sub && <p className="text-sm mt-0.5" style={{ color: C.mute }}>{sub}</p>}</div>
    </div>
    {action}
  </div>
);
const AddBtn = ({ onClick, label, testid }) => (
  <button onClick={onClick} className="shrink-0 flex items-center gap-1.5 text-sm font-semibold px-3.5 h-9 rounded-full text-white" style={{ background: C.navy }} data-testid={testid}><Plus className="h-4 w-4" /> {label}</button>
);
const inp = 'w-full h-11 px-3 rounded-xl border border-[#E3E1DC] text-sm outline-none focus:border-[#2B4C7E]';
const Lbl = ({ children }) => <label className="block text-xs font-semibold mb-1" style={{ color: '#57534E' }}>{children}</label>;

function Modal({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-lg max-h-[88vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid={testid}>
        <div className="flex items-center justify-between mb-4"><h3 className="text-lg font-bold" style={{ color: C.ink }}>{title}</h3><button onClick={onClose}><X className="h-5 w-5" style={{ color: C.mute }} /></button></div>
        {children}
      </div>
    </div>
  );
}
const SaveBar = ({ onCancel, onSave, saving, testid }) => (
  <div className="flex justify-end gap-2 pt-2">
    <button onClick={onCancel} className="h-10 px-5 rounded-xl border font-medium text-sm" style={{ borderColor: C.line, color: C.ink }}>Cancel</button>
    <button onClick={onSave} disabled={saving} className="h-10 px-6 rounded-xl text-white font-medium text-sm flex items-center gap-2 disabled:opacity-60" style={{ background: C.navy }} data-testid={testid}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save</button>
  </div>
);

// ---------- Education ----------
function Education() {
  const [open, setOpen] = useState(null);
  return (
    <Card testid="savings-education">
      <SectionHead icon={Landmark} title="Why a Savings Account Matters" />
      <p className="text-sm leading-relaxed" style={{ color: '#4A5560' }}>
        A savings account can help your family prepare for emergencies, final expenses, household needs, and future planning.
        Keeping funds in a trusted financial institution can make it easier for loved ones to understand where money is held and who should be contacted when help is needed.
      </p>
      <div className="mt-4 grid sm:grid-cols-2 gap-2">
        {EDU_TOPICS.map((t) => <div key={t} className="flex items-start gap-2 text-sm" style={{ color: '#4A5560' }}><ShieldCheck className="h-4 w-4 shrink-0 mt-0.5" style={{ color: C.green }} />{t}</div>)}
      </div>
      <div className="mt-5 space-y-2">
        {FAQS.map(([q, a], i) => (
          <div key={i} className="border border-[#EDE7DC] rounded-xl overflow-hidden">
            <button onClick={() => setOpen(open === i ? null : i)} className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left" data-testid={`faq-${i}`}>
              <span className="text-sm font-medium" style={{ color: C.ink }}>{q}</span>
              <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${open === i ? 'rotate-180' : ''}`} style={{ color: C.mute }} />
            </button>
            {open === i && <p className="px-4 pb-4 text-sm" style={{ color: '#4A5560' }}>{a}</p>}
          </div>
        ))}
      </div>
    </Card>
  );
}

// ---------- Bank Finder ----------
function BankFinder({ myBanks, reloadBanks }) {
  const [q, setQ] = useState('');
  const [kind, setKind] = useState('bank');
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notConfigured, setNotConfigured] = useState(false);
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState('');

  const search = async (opts = {}) => {
    setBusy(true); setResults(null); setNote('');
    const usingLocation = !!opts.lat;
    try {
      const params = { kind, ...opts };
      if (!opts.lat && q) params.query = q;
      const r = await axios.get(`${API}/savings/banks/nearby`, { ...authH(), params });
      if (r.data.configured === false) { setNotConfigured(true); setResults([]); }
      else {
        setNotConfigured(false);
        const list = r.data.banks || [];
        setResults(list);
        const noun = kind === 'credit_union' ? (list.length === 1 ? 'credit union' : 'credit unions') : (list.length === 1 ? 'bank' : 'banks');
        const where = usingLocation ? 'near you' : (q ? `near ${q}` : 'near you');
        setNote(list.length ? `${list.length} ${noun} found ${where}` : '');
        if (r.data.message && !list.length) toast.info(r.data.message);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Search failed'); }
    finally { setBusy(false); }
  };

  const useLocation = () => {
    if (!navigator.geolocation) { toast.error('Location not available'); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => search({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => toast.error('Could not get your location'),
    );
  };

  const openDetail = async (b) => {
    setDetail({ ...b, loading: true });
    try { const r = await axios.get(`${API}/savings/banks/${b.place_id}`, authH()); setDetail({ ...b, ...r.data, loading: false }); }
    catch { setDetail({ ...b, loading: false }); }
  };

  const saveBank = async (b) => {
    try {
      await axios.post(`${API}/savings/my-banks`, { place_id: b.place_id, name: b.name, address: b.address, phone: b.phone, website: b.website }, authH());
      toast.success('Saved as My Bank'); reloadBanks();
    } catch { toast.error('Could not save'); }
  };
  const removeBank = async (id) => { try { await axios.delete(`${API}/savings/my-banks/${id}`, authH()); toast.success('Removed'); reloadBanks(); } catch { toast.error('Failed'); } };

  return (
    <Card testid="savings-bank-finder">
      <SectionHead icon={Search} title="Find a Bank Near You" sub="Search banks and credit unions by ZIP code, city, or your current location." />
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: C.mute }} />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && search()} placeholder="ZIP code or city" className={`${inp} pl-9`} data-testid="bank-search-input" />
        </div>
        <button onClick={() => search()} disabled={busy} className="h-11 px-5 rounded-xl text-white text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: C.navy }} data-testid="bank-search-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Search</button>
        <button onClick={useLocation} className="h-11 px-4 rounded-xl border text-sm font-medium flex items-center justify-center gap-2" style={{ borderColor: C.line, color: C.navy }} data-testid="bank-use-location"><Navigation className="h-4 w-4" /> Near me</button>
      </div>

      <div className="mt-3 inline-flex items-center bg-[#F1EEE8] rounded-full p-1" data-testid="bank-kind-toggle">
        {[['bank', 'All banks'], ['credit_union', 'Credit unions only']].map(([v, label]) => (
          <button key={v} onClick={() => setKind(v)} data-testid={`bank-kind-${v}`}
            className={`px-4 h-8 rounded-full text-xs font-semibold transition-colors ${kind === v ? 'bg-white shadow-sm' : ''}`}
            style={{ color: kind === v ? C.navy : C.mute }}>{label}</button>
        ))}
      </div>

      {notConfigured && <p className="mt-4 text-sm rounded-xl p-3" style={{ background: '#EBF0F5', color: C.navy2 }}>Live bank search will be available once the map service is connected. You can still add your bank manually below.</p>}

      {results && !notConfigured && (
        <div className="mt-4 space-y-2" data-testid="bank-results">
          {note && <p className="text-sm font-semibold" style={{ color: C.navy }} data-testid="bank-count-note">{note}</p>}
          {results.length === 0 && <p className="text-sm" style={{ color: C.mute }}>No results. Try a different ZIP or city.</p>}
          {results.map((b) => (
            <div key={b.place_id} className="border border-[#EDE7DC] rounded-xl p-4 flex items-start justify-between gap-3">
              <button onClick={() => openDetail(b)} className="text-left min-w-0 flex-1">
                <p className="font-semibold text-sm" style={{ color: C.ink }}>{b.name}</p>
                <p className="text-xs mt-0.5" style={{ color: C.mute }}>{b.address}</p>
                {b.rating != null && <p className="text-xs mt-1 flex items-center gap-1" style={{ color: C.gold }}><Star className="h-3 w-3 fill-current" /> {b.rating} · {b.user_ratings_total || 0} reviews</p>}
              </button>
              <div className="flex flex-col gap-1.5 shrink-0">
                <a href={b.directions_url} target="_blank" rel="noreferrer" className="text-xs px-3 py-1.5 rounded-full border flex items-center gap-1" style={{ borderColor: C.line, color: C.navy }}><Navigation className="h-3.5 w-3.5" /> Directions</a>
                <button onClick={() => saveBank(b)} className="text-xs px-3 py-1.5 rounded-full text-white flex items-center gap-1" style={{ background: C.green }}><Bookmark className="h-3.5 w-3.5" /> My Bank</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {myBanks.length > 0 && (
        <div className="mt-5">
          <p className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: C.mute }}>My saved banks</p>
          <div className="space-y-2">
            {myBanks.map((b) => (
              <div key={b.saved_bank_id} className="border border-[#EDE7DC] rounded-xl p-3 flex items-center justify-between gap-3">
                <div className="min-w-0"><p className="font-semibold text-sm truncate" style={{ color: C.ink }}>{b.name}</p><p className="text-xs truncate" style={{ color: C.mute }}>{b.address}</p></div>
                <button onClick={() => removeBank(b.saved_bank_id)} className="shrink-0 p-1.5 rounded-lg border" style={{ borderColor: C.line, color: '#C0563F' }}><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="mt-4 text-xs" style={{ color: C.mute }}>Bank listings are for convenience only. EasyPassing does not endorse, guarantee, or control any financial institution.</p>

      {detail && (
        <Modal title={detail.name} onClose={() => setDetail(null)} testid="bank-detail-modal">
          {detail.loading ? <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin" style={{ color: C.navy }} /></div> : (
            <div className="space-y-3 text-sm" style={{ color: '#4A5560' }}>
              {detail.address && <p className="flex items-start gap-2"><MapPin className="h-4 w-4 mt-0.5 shrink-0" style={{ color: C.navy }} /> {detail.address}</p>}
              {detail.phone && <a href={`tel:${detail.phone}`} className="flex items-center gap-2 font-medium" style={{ color: C.navy }}><Phone className="h-4 w-4" /> {detail.phone}</a>}
              {detail.website && <a href={detail.website} target="_blank" rel="noreferrer" className="flex items-center gap-2 font-medium truncate" style={{ color: C.navy }}><Globe className="h-4 w-4 shrink-0" /> Visit website</a>}
              <div className="flex gap-2 pt-2">
                <a href={detail.directions_url || detail.maps_url} target="_blank" rel="noreferrer" className="flex-1 text-center text-sm px-3 py-2 rounded-xl border font-medium" style={{ borderColor: C.line, color: C.navy }}>Directions</a>
                <button onClick={() => { saveBank(detail); setDetail(null); }} className="flex-1 text-sm px-3 py-2 rounded-xl text-white font-medium" style={{ background: C.green }}>Save as My Bank</button>
              </div>
            </div>
          )}
        </Modal>
      )}
    </Card>
  );
}

// ---------- Savings Accounts ----------
function SavingsAccounts({ accounts, reload }) {
  const [editing, setEditing] = useState(null);
  const empty = { bank_name: '', account_nickname: '', account_type: 'savings', masked_account_number: '', current_balance: '', is_primary: false, notes: '' };
  return (
    <Card testid="savings-accounts">
      <SectionHead icon={Landmark} title="Connected Savings Account" sub="Add your savings accounts to track balances. Your money stays with your bank." action={<AddBtn onClick={() => setEditing(empty)} label="Add account" testid="add-account-btn" />} />
      <p className="text-xs mb-3 rounded-lg px-3 py-2" style={{ background: '#EBF0F5', color: C.navy2 }}>Store only a masked account number (last 4 digits). Never enter full account numbers or bank passwords. Automatic secure linking is coming soon.</p>
      {accounts.length === 0 ? <Empty text="No savings accounts added yet." /> : (
        <div className="grid sm:grid-cols-2 gap-3">
          {accounts.map((a) => (
            <div key={a.savings_account_id} className="border border-[#EDE7DC] rounded-xl p-4" data-testid={`account-${a.savings_account_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold truncate" style={{ color: C.ink }}>{a.account_nickname || a.bank_name}</p>
                  <p className="text-xs" style={{ color: C.mute }}>{a.bank_name} · {(ACCOUNT_TYPES.find((t) => t[0] === a.account_type) || [])[1]}</p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => setEditing({ ...a, current_balance: a.current_balance ?? '' })} className="p-1.5 rounded-lg border" style={{ borderColor: C.line, color: C.navy }}><Pencil className="h-3.5 w-3.5" /></button>
                  <button onClick={async () => { if (window.confirm('Delete this account?')) { await axios.delete(`${API}/savings/accounts/${a.savings_account_id}`, authH()); toast.success('Deleted'); reload(); } }} className="p-1.5 rounded-lg border" style={{ borderColor: C.line, color: '#C0563F' }}><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
              <p className="mt-2 font-display text-2xl font-bold" style={{ color: C.navy }}>{money(a.current_balance)}</p>
              <div className="flex items-center gap-2 mt-1 text-xs" style={{ color: C.mute }}>
                {a.masked_account_number && <span>****{a.masked_account_number}</span>}
                {a.is_primary && <span className="px-2 py-0.5 rounded-full font-semibold" style={{ background: '#EAF3EE', color: C.green }}>Primary legacy savings</span>}
              </div>
            </div>
          ))}
        </div>
      )}
      {editing && <AccountForm form={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />}
    </Card>
  );
}
function AccountForm({ form, onClose, onSaved }) {
  const [f, setF] = useState(form); const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.bank_name.trim()) { toast.error('Bank name is required'); return; }
    setSaving(true);
    const payload = { ...f, current_balance: f.current_balance === '' ? 0 : Number(f.current_balance), masked_account_number: (f.masked_account_number || '').replace(/\D/g, '').slice(-4) };
    try {
      if (f.savings_account_id) await axios.put(`${API}/savings/accounts/${f.savings_account_id}`, payload, authH());
      else await axios.post(`${API}/savings/accounts`, payload, authH());
      toast.success('Saved'); onSaved();
    } catch { toast.error('Save failed'); setSaving(false); }
  };
  return (
    <Modal title={f.savings_account_id ? 'Edit account' : 'Add savings account'} onClose={onClose} testid="account-form">
      <div className="space-y-3">
        <div><Lbl>Bank name</Lbl><input value={f.bank_name} onChange={(e) => set('bank_name', e.target.value)} className={inp} data-testid="account-bank-name" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Lbl>Nickname</Lbl><input value={f.account_nickname || ''} onChange={(e) => set('account_nickname', e.target.value)} className={inp} /></div>
          <div><Lbl>Account type</Lbl><select value={f.account_type} onChange={(e) => set('account_type', e.target.value)} className={inp}>{ACCOUNT_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Lbl>Last 4 digits</Lbl><input value={f.masked_account_number || ''} onChange={(e) => set('masked_account_number', e.target.value)} placeholder="1234" maxLength={4} className={inp} /></div>
          <div><Lbl>Current balance ($)</Lbl><input type="number" value={f.current_balance} onChange={(e) => set('current_balance', e.target.value)} className={inp} data-testid="account-balance" /></div>
        </div>
        <label className="flex items-center gap-2 text-sm" style={{ color: C.ink }}><input type="checkbox" checked={!!f.is_primary} onChange={(e) => set('is_primary', e.target.checked)} /> Mark as primary legacy savings account</label>
        <div><Lbl>Notes</Lbl><textarea value={f.notes || ''} onChange={(e) => set('notes', e.target.value)} rows={2} className="w-full px-3 py-2 rounded-xl border border-[#E3E1DC] text-sm outline-none resize-none" /></div>
        <SaveBar onCancel={onClose} onSave={save} saving={saving} testid="account-save" />
      </div>
    </Modal>
  );
}

// ---------- Savings Goals ----------
function SavingsGoals({ goals, accounts, reload }) {
  const [editing, setEditing] = useState(null);
  const empty = { goal_name: '', target_amount: '', current_amount: '', monthly_goal: '', reminder_frequency: 'monthly', savings_account_id: '' };
  return (
    <Card testid="savings-goals">
      <SectionHead icon={Target} title="Savings Goal" sub="Set a target and track your progress. EasyPassing helps you track — your money stays with your bank." action={<AddBtn onClick={() => setEditing(empty)} label="Add goal" testid="add-goal-btn" />} />
      {goals.length === 0 ? <Empty text="No savings goals yet." /> : (
        <div className="space-y-3">
          {goals.map((g) => {
            const pct = g.target_amount ? Math.min(100, Math.round((Number(g.current_amount || 0) / Number(g.target_amount)) * 100)) : 0;
            return (
              <div key={g.goal_id} className="border border-[#EDE7DC] rounded-xl p-4" data-testid={`goal-${g.goal_id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div><p className="font-semibold" style={{ color: C.ink }}>{g.goal_name}</p><p className="text-xs" style={{ color: C.mute }}>{money(g.current_amount)} of {money(g.target_amount)}{g.monthly_goal ? ` · ${money(g.monthly_goal)}/mo` : ''}</p></div>
                  <div className="flex gap-1 shrink-0">
                    <button onClick={() => setEditing({ ...g, target_amount: g.target_amount ?? '', current_amount: g.current_amount ?? '', monthly_goal: g.monthly_goal ?? '' })} className="p-1.5 rounded-lg border" style={{ borderColor: C.line, color: C.navy }}><Pencil className="h-3.5 w-3.5" /></button>
                    <button onClick={async () => { if (window.confirm('Delete goal?')) { await axios.delete(`${API}/savings/goals/${g.goal_id}`, authH()); toast.success('Deleted'); reload(); } }} className="p-1.5 rounded-lg border" style={{ borderColor: C.line, color: '#C0563F' }}><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
                <div className="mt-3 h-2.5 rounded-full bg-[#EFEAE0] overflow-hidden"><div className="h-full rounded-full" style={{ width: `${pct}%`, background: C.green }} /></div>
                <p className="text-xs mt-1 font-semibold" style={{ color: C.green }}>{pct}% funded</p>
              </div>
            );
          })}
        </div>
      )}
      {editing && <GoalForm form={editing} accounts={accounts} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />}
    </Card>
  );
}
function GoalForm({ form, accounts, onClose, onSaved }) {
  const [f, setF] = useState(form); const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.goal_name.trim() || !f.target_amount) { toast.error('Goal name and target are required'); return; }
    setSaving(true);
    const payload = { ...f, target_amount: Number(f.target_amount), current_amount: f.current_amount === '' ? 0 : Number(f.current_amount), monthly_goal: f.monthly_goal === '' ? null : Number(f.monthly_goal), savings_account_id: f.savings_account_id || null };
    try {
      if (f.goal_id) await axios.put(`${API}/savings/goals/${f.goal_id}`, payload, authH());
      else await axios.post(`${API}/savings/goals`, payload, authH());
      toast.success('Saved'); onSaved();
    } catch { toast.error('Save failed'); setSaving(false); }
  };
  return (
    <Modal title={f.goal_id ? 'Edit goal' : 'Add savings goal'} onClose={onClose} testid="goal-form">
      <div className="space-y-3">
        <div><Lbl>Goal name</Lbl><input value={f.goal_name} onChange={(e) => set('goal_name', e.target.value)} className={inp} data-testid="goal-name" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Lbl>Target amount ($)</Lbl><input type="number" value={f.target_amount} onChange={(e) => set('target_amount', e.target.value)} className={inp} data-testid="goal-target" /></div>
          <div><Lbl>Current amount ($)</Lbl><input type="number" value={f.current_amount} onChange={(e) => set('current_amount', e.target.value)} className={inp} /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Lbl>Monthly goal ($)</Lbl><input type="number" value={f.monthly_goal} onChange={(e) => set('monthly_goal', e.target.value)} className={inp} /></div>
          <div><Lbl>Reminder</Lbl><select value={f.reminder_frequency} onChange={(e) => set('reminder_frequency', e.target.value)} className={inp}><option value="none">None</option><option value="weekly">Weekly</option><option value="monthly">Monthly</option><option value="quarterly">Quarterly</option></select></div>
        </div>
        {accounts.length > 0 && <div><Lbl>Linked account (optional)</Lbl><select value={f.savings_account_id || ''} onChange={(e) => set('savings_account_id', e.target.value)} className={inp}><option value="">None</option>{accounts.map((a) => <option key={a.savings_account_id} value={a.savings_account_id}>{a.account_nickname || a.bank_name}</option>)}</select></div>}
        <SaveBar onCancel={onClose} onSave={save} saving={saving} testid="goal-save" />
      </div>
    </Modal>
  );
}

// ---------- Deposits ----------
function Deposits({ deposits, accounts, reload }) {
  const [editing, setEditing] = useState(null);
  const empty = { amount: '', deposit_method: 'manual_entry', deposit_date: '', notes: '', savings_account_id: '' };
  return (
    <Card testid="savings-deposits">
      <SectionHead icon={PiggyBank} title="Add Money to Your Savings" sub="Make deposits directly into your connected savings account using supported bank options. EasyPassing tracks your goal — your money stays with your bank." action={<AddBtn onClick={() => setEditing(empty)} label="Log deposit" testid="add-deposit-btn" />} />
      <div className="flex flex-wrap gap-2 mb-4">
        {[['Open bank app', 'https://'], ['Visit bank website', 'https://']].map(([l]) => <span key={l} className="text-xs px-3 py-1.5 rounded-full border" style={{ borderColor: C.line, color: C.mute }}>{l} (use your bank)</span>)}
      </div>
      {deposits.deposits.length > 0 && <p className="text-sm mb-3 font-semibold" style={{ color: C.ink }}>Total logged: <span style={{ color: C.green }}>{money(deposits.total_deposited)}</span></p>}
      {deposits.deposits.length === 0 ? <Empty text="No deposits logged yet." /> : (
        <div className="space-y-2">
          {deposits.deposits.map((d) => (
            <div key={d.deposit_id} className="border border-[#EDE7DC] rounded-xl p-3 flex items-center justify-between gap-3" data-testid={`deposit-${d.deposit_id}`}>
              <div className="min-w-0"><p className="font-semibold text-sm" style={{ color: C.ink }}>{money(d.amount)}</p><p className="text-xs" style={{ color: C.mute }}>{(DEPOSIT_METHODS.find((m) => m[0] === d.deposit_method) || [])[1]} · {new Date(d.deposit_date).toLocaleDateString()}</p></div>
              <button onClick={async () => { await axios.delete(`${API}/savings/deposits/${d.deposit_id}`, authH()); toast.success('Deleted'); reload(); }} className="shrink-0 p-1.5 rounded-lg border" style={{ borderColor: C.line, color: '#C0563F' }}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}
      {editing && <DepositForm form={editing} accounts={accounts} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />}
    </Card>
  );
}
function DepositForm({ form, accounts, onClose, onSaved }) {
  const [f, setF] = useState(form); const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.amount) { toast.error('Amount is required'); return; }
    setSaving(true);
    const payload = { ...f, amount: Number(f.amount), deposit_date: f.deposit_date || undefined, savings_account_id: f.savings_account_id || null };
    try { await axios.post(`${API}/savings/deposits`, payload, authH()); toast.success('Deposit logged'); onSaved(); }
    catch { toast.error('Save failed'); setSaving(false); }
  };
  return (
    <Modal title="Log a deposit" onClose={onClose} testid="deposit-form">
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div><Lbl>Amount ($)</Lbl><input type="number" value={f.amount} onChange={(e) => set('amount', e.target.value)} className={inp} data-testid="deposit-amount" /></div>
          <div><Lbl>Date</Lbl><input type="date" value={f.deposit_date} onChange={(e) => set('deposit_date', e.target.value)} className={inp} /></div>
        </div>
        <div><Lbl>Method</Lbl><select value={f.deposit_method} onChange={(e) => set('deposit_method', e.target.value)} className={inp}>{DEPOSIT_METHODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
        {accounts.length > 0 && <div><Lbl>Account (optional)</Lbl><select value={f.savings_account_id || ''} onChange={(e) => set('savings_account_id', e.target.value)} className={inp}><option value="">None</option>{accounts.map((a) => <option key={a.savings_account_id} value={a.savings_account_id}>{a.account_nickname || a.bank_name}</option>)}</select></div>}
        <div><Lbl>Note</Lbl><input value={f.notes || ''} onChange={(e) => set('notes', e.target.value)} className={inp} /></div>
        <SaveBar onCancel={onClose} onSave={save} saving={saving} testid="deposit-save" />
      </div>
    </Modal>
  );
}

// ---------- Beneficiaries ----------
function Beneficiaries({ bens, reload }) {
  const [editing, setEditing] = useState(null);
  const empty = { full_name: '', relationship: '', email: '', phone: '', mailing_address: '', distribution_type: 'instructions_only', percentage_amount: '', fixed_amount: '', notes_to_beneficiary: '', should_receive_release_notice: true, linked_plaid_item_id: '', linked_plaid_account_id: '', linked_plaid_account_name: '' };
  return (
    <Card testid="savings-beneficiaries-section">
      <SectionHead icon={Users} title="Beneficiary Instructions" sub="List people to inform about your savings account and your intended instructions." action={<AddBtn onClick={() => setEditing(empty)} label="Add beneficiary" testid="add-beneficiary-btn" />} />
      <p className="text-xs mb-3 rounded-lg px-3 py-2" style={{ background: '#FFF9EC', color: '#8a6d1f' }}>Adding a person here does not automatically make them a legal beneficiary of your bank account. To legally name beneficiaries, contact your bank and/or estate attorney.</p>
      {bens.allocation_warning && (
        <p className="text-xs mb-3 rounded-lg px-3 py-2 font-semibold" style={{ background: '#FCECE8', color: '#C0563F' }}>
          {bens.allocation_warning === 'over' ? `Your percentage allocations total ${bens.total_percentage}% — over 100%.` : `Your percentage allocations total ${bens.total_percentage}% — under 100%.`}
        </p>
      )}
      {bens.beneficiaries.length === 0 ? <Empty text="No beneficiary instructions yet." /> : (
        <div className="space-y-2">
          {bens.beneficiaries.map((b) => (
            <div key={b.beneficiary_id} className="border border-[#EDE7DC] rounded-xl p-4" data-testid={`beneficiary-${b.beneficiary_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold" style={{ color: C.ink }}>{b.full_name} {b.relationship && <span className="font-normal text-xs" style={{ color: C.mute }}>· {b.relationship}</span>}</p>
                  <p className="text-xs mt-0.5" style={{ color: C.mute }}>
                    {(DIST_TYPES.find((d) => d[0] === b.distribution_type) || [])[1]}
                    {b.distribution_type === 'percentage' && b.percentage_amount != null ? ` · ${b.percentage_amount}%` : ''}
                    {b.distribution_type === 'fixed_amount' && b.fixed_amount != null ? ` · ${money(b.fixed_amount)}` : ''}
                  </p>
                  {b.notes_to_beneficiary && <p className="text-sm mt-1" style={{ color: '#4A5560' }}>“{b.notes_to_beneficiary}”</p>}
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => setEditing({ ...b, percentage_amount: b.percentage_amount ?? '', fixed_amount: b.fixed_amount ?? '' })} className="p-1.5 rounded-lg border" style={{ borderColor: C.line, color: C.navy }}><Pencil className="h-3.5 w-3.5" /></button>
                  <button onClick={async () => { if (window.confirm('Delete beneficiary?')) { await axios.delete(`${API}/savings/beneficiaries/${b.beneficiary_id}`, authH()); toast.success('Deleted'); reload(); } }} className="p-1.5 rounded-lg border" style={{ borderColor: C.line, color: '#C0563F' }}><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {editing && <BeneficiaryForm form={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />}
    </Card>
  );
}
function BeneficiaryForm({ form, onClose, onSaved }) {
  const [f, setF] = useState(form); const [saving, setSaving] = useState(false);
  const [plaidAccts, setPlaidAccts] = useState([]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  useEffect(() => {
    axios.get(`${API}/savings/plaid/accounts`, authH())
      .then((r) => setPlaidAccts((r.data.items || []).flatMap((it) => (it.accounts || []).map((a) => ({ ...a, item_id: it.item_id, institution_name: it.institution_name })))))
      .catch(() => {});
  }, []);
  const onPickPlaid = (acctId) => {
    const a = plaidAccts.find((x) => x.account_id === acctId);
    setF((p) => ({ ...p, linked_plaid_account_id: acctId || '', linked_plaid_item_id: a ? a.item_id : '', linked_plaid_account_name: a ? `${a.institution_name} · ${a.name}` : '' }));
  };
  const save = async () => {
    if (!f.full_name.trim()) { toast.error('Full name is required'); return; }
    setSaving(true);
    const payload = { ...f, email: f.email || null, percentage_amount: f.percentage_amount === '' ? null : Number(f.percentage_amount), fixed_amount: f.fixed_amount === '' ? null : Number(f.fixed_amount), linked_plaid_item_id: f.linked_plaid_item_id || null, linked_plaid_account_id: f.linked_plaid_account_id || null, linked_plaid_account_name: f.linked_plaid_account_name || null };
    try {
      if (f.beneficiary_id) await axios.put(`${API}/savings/beneficiaries/${f.beneficiary_id}`, payload, authH());
      else await axios.post(`${API}/savings/beneficiaries`, payload, authH());
      toast.success('Saved'); onSaved();
    } catch { toast.error('Save failed'); setSaving(false); }
  };
  return (
    <Modal title={f.beneficiary_id ? 'Edit beneficiary' : 'Add beneficiary'} onClose={onClose} testid="beneficiary-form">
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div><Lbl>Full name</Lbl><input value={f.full_name} onChange={(e) => set('full_name', e.target.value)} className={inp} data-testid="beneficiary-name" /></div>
          <div><Lbl>Relationship</Lbl><input value={f.relationship || ''} onChange={(e) => set('relationship', e.target.value)} className={inp} /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Lbl>Email</Lbl><input type="email" value={f.email || ''} onChange={(e) => set('email', e.target.value)} className={inp} /></div>
          <div><Lbl>Phone</Lbl><input value={f.phone || ''} onChange={(e) => set('phone', e.target.value)} className={inp} /></div>
        </div>
        <div><Lbl>Mailing address</Lbl><input value={f.mailing_address || ''} onChange={(e) => set('mailing_address', e.target.value)} className={inp} /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Lbl>Distribution type</Lbl><select value={f.distribution_type} onChange={(e) => set('distribution_type', e.target.value)} className={inp} data-testid="beneficiary-dist-type">{DIST_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
          {f.distribution_type === 'percentage' && <div><Lbl>Percentage (%)</Lbl><input type="number" value={f.percentage_amount} onChange={(e) => set('percentage_amount', e.target.value)} className={inp} data-testid="beneficiary-pct" /></div>}
          {f.distribution_type === 'fixed_amount' && <div><Lbl>Amount ($)</Lbl><input type="number" value={f.fixed_amount} onChange={(e) => set('fixed_amount', e.target.value)} className={inp} /></div>}
        </div>
        <div><Lbl>Message to beneficiary</Lbl><textarea value={f.notes_to_beneficiary || ''} onChange={(e) => set('notes_to_beneficiary', e.target.value)} rows={2} placeholder="Please contact the bank and executor for next steps." className="w-full px-3 py-2 rounded-xl border border-[#E3E1DC] text-sm outline-none resize-none" /></div>
        {plaidAccts.length > 0 && (
          <div><Lbl>Link a bank account (optional)</Lbl>
            <select value={f.linked_plaid_account_id || ''} onChange={(e) => onPickPlaid(e.target.value)} className={inp} data-testid="beneficiary-plaid-account">
              <option value="">— None —</option>
              {plaidAccts.map((a) => <option key={a.account_id} value={a.account_id}>{a.institution_name} · {a.name}{a.mask ? ` ••••${a.mask}` : ''}</option>)}
            </select>
            <p className="text-xs mt-1" style={{ color: C.mute }}>The account's current balance will be included in this beneficiary's release packet.</p>
          </div>
        )}
        <label className="flex items-center gap-2 text-sm" style={{ color: C.ink }}><input type="checkbox" checked={!!f.should_receive_release_notice} onChange={(e) => set('should_receive_release_notice', e.target.checked)} /> Notify this person when information is released</label>
        <SaveBar onCancel={onClose} onSave={save} saving={saving} testid="beneficiary-save" />
      </div>
    </Modal>
  );
}

// ---------- Executors ----------
const EXEC_STATUS = { invited: ['Invited', C.gold], accepted: ['Accepted', C.green], declined: ['Declined', '#C0563F'], removed: ['Removed', C.mute], pending_verification: ['Pending verification', C.gold], verified: ['Verified', C.green] };
function Executors({ execs, reload }) {
  const [editing, setEditing] = useState(null);
  const primary = execs.find((e) => e.executor_type === 'primary');
  const backups = execs.filter((e) => e.executor_type === 'backup');
  const invite = async (e) => { try { const r = await axios.post(`${API}/savings/executors/${e.executor_id}/invite`, {}, authH()); toast.success(`Invitation sent to ${r.data.sent_to || 'executor'}`); reload(); } catch { toast.error('Could not send invite'); } };
  const del = async (e) => { if (!window.confirm('Remove this executor?')) return; await axios.delete(`${API}/savings/executors/${e.executor_id}`, authH()); toast.success('Removed'); reload(); };
  const Row = ({ e }) => {
    const [label, color] = EXEC_STATUS[e.access_status] || ['Invited', C.gold];
    return (
      <div className="border border-[#EDE7DC] rounded-xl p-4" data-testid={`executor-${e.executor_id}`}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="font-semibold" style={{ color: C.ink }}>{e.full_name} <span className="text-xs font-normal capitalize" style={{ color: C.mute }}>· {e.executor_type}</span></p>
            <p className="text-xs mt-0.5 truncate" style={{ color: C.mute }}>{e.email}{e.relationship ? ` · ${e.relationship}` : ''}</p>
            <span className="inline-block mt-1.5 text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: `${color}22`, color }}>{label}</span>
          </div>
          <div className="flex gap-1 shrink-0">
            <button onClick={() => invite(e)} title="Send invite / reminder" className="p-1.5 rounded-lg border" style={{ borderColor: C.line, color: C.navy }}><Send className="h-3.5 w-3.5" /></button>
            <button onClick={() => setEditing(e)} className="p-1.5 rounded-lg border" style={{ borderColor: C.line, color: C.navy }}><Pencil className="h-3.5 w-3.5" /></button>
            <button onClick={() => del(e)} className="p-1.5 rounded-lg border" style={{ borderColor: C.line, color: '#C0563F' }}><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        </div>
      </div>
    );
  };
  return (
    <Card testid="savings-executors">
      <SectionHead icon={ShieldCheck} title="Primary Executor" sub="Your trusted person who can release your saved account location and beneficiary instructions after you pass." action={<AddBtn onClick={() => setEditing({ executor_type: primary ? 'backup' : 'primary', full_name: '', relationship: '', email: '', phone: '', mailing_address: '' })} label={primary ? 'Add backup' : 'Invite executor'} testid="add-executor-btn" />} />
      <p className="text-xs mb-3" style={{ color: C.mute }}>Choose a Primary Executor you trust deeply. They may receive access to sensitive information after your passing. A Backup Executor only gains release ability if the primary is removed, declines, or cannot be reached.</p>
      <div className="space-y-2">
        {primary ? <Row e={primary} /> : <Empty text="No Primary Executor chosen yet." />}
        {backups.map((e) => <Row key={e.executor_id} e={e} />)}
      </div>
      {editing && <ExecutorForm form={editing} hasPrimary={!!primary} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />}
    </Card>
  );
}
function ExecutorForm({ form, hasPrimary, onClose, onSaved }) {
  const [f, setF] = useState(form); const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.full_name.trim() || !f.email.trim()) { toast.error('Name and email are required'); return; }
    setSaving(true);
    try {
      if (f.executor_id) await axios.put(`${API}/savings/executors/${f.executor_id}`, f, authH());
      else await axios.post(`${API}/savings/executors`, f, authH());
      toast.success('Saved'); onSaved();
    } catch { toast.error('Save failed'); setSaving(false); }
  };
  return (
    <Modal title={f.executor_id ? 'Edit executor' : 'Add executor'} onClose={onClose} testid="executor-form">
      <div className="space-y-3">
        <div><Lbl>Executor type</Lbl><select value={f.executor_type} onChange={(e) => set('executor_type', e.target.value)} className={inp} data-testid="executor-type"><option value="primary">Primary{hasPrimary && !f.executor_id ? ' (will replace current)' : ''}</option><option value="backup">Backup</option></select></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Lbl>Full name</Lbl><input value={f.full_name} onChange={(e) => set('full_name', e.target.value)} className={inp} data-testid="executor-name" /></div>
          <div><Lbl>Relationship</Lbl><input value={f.relationship || ''} onChange={(e) => set('relationship', e.target.value)} className={inp} /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Lbl>Email</Lbl><input type="email" value={f.email} onChange={(e) => set('email', e.target.value)} className={inp} data-testid="executor-email" /></div>
          <div><Lbl>Phone</Lbl><input value={f.phone || ''} onChange={(e) => set('phone', e.target.value)} className={inp} /></div>
        </div>
        <div><Lbl>Mailing address</Lbl><input value={f.mailing_address || ''} onChange={(e) => set('mailing_address', e.target.value)} className={inp} /></div>
        <SaveBar onCancel={onClose} onSave={save} saving={saving} testid="executor-save" />
      </div>
    </Modal>
  );
}

// ---------- Aftercare ----------
function Aftercare({ aftercare, navigate, onBuy }) {
  if (!aftercare) return null;
  return (
    <Card testid="savings-aftercare">
      <SectionHead icon={Clock} title="After-Passing Access (Aftercare)" sub="How long your account, Whispers, and beneficiary portal stay active after release." />
      <div className="rounded-xl p-4 flex items-center justify-between gap-3" style={{ background: '#EBF0F5' }}>
        <div><p className="text-xs font-medium uppercase tracking-wide" style={{ color: C.navy2 }}>Included with your {aftercare.plan === 'free' ? 'account' : `${aftercare.plan.replace('_', ' ')} plan`}</p><p className="font-display text-2xl font-bold" style={{ color: C.navy }}>{aftercare.included_label} of aftercare</p></div>
        <Building2 className="h-8 w-8" style={{ color: C.navy2, opacity: 0.4 }} />
      </div>
      <div className="mt-3 grid sm:grid-cols-3 gap-3">
        {aftercare.options.map((o) => (
          <div key={o.id} className="border border-[#EDE7DC] rounded-xl p-4 text-center flex flex-col" data-testid={`aftercare-option-${o.id}`}>
            <p className="font-semibold text-sm" style={{ color: C.ink }}>{o.name}</p>
            <p className="font-display text-xl font-bold mt-1" style={{ color: C.gold }}>{money(o.price)}</p>
            <p className="text-xs mb-3" style={{ color: C.mute }}>one-time</p>
            <button onClick={() => onBuy(o.id)} className="mt-auto text-sm font-semibold h-9 rounded-full text-white" style={{ background: C.navy }} data-testid={`aftercare-buy-${o.id}`}>Choose</button>
          </div>
        ))}
      </div>
      <p className="text-xs mt-3" style={{ color: C.mute }}>{aftercare.note} <button onClick={() => navigate('/membership')} className="font-semibold underline" style={{ color: C.navy }}>View plans</button></p>
    </Card>
  );
}

const Empty = ({ text }) => <div className="text-center py-10 text-sm" style={{ color: C.mute }} data-testid="savings-empty">{text}</div>;
